import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';
import openNotification from '../../components/Common/OpenNotification';
import { ADMIN_CUSTOMER_GROUP_LIST, ADMIN_DASHBOARD_BUSINESS_SECTORWISE_REVENUE, ADMIN_DASHBOARD_CUSTOM_CARD, ADMIN_PRIORITY_UNITS, ADMIN_PROJECT_REPORT_CUSTOMER_GROUP_WISE_LISTING, ADMIN_REVENUE_GRAPH, ADMIN_TOP_10_PROJECT_LISTING, ADMIN_TOP_10_PROJECT_WITHIN_FY, ADMIN_TOP_10_PROJECTS } from '../../api/endpoints';
import { RemoveEmptyKey } from '../../utils/removeEmptyKey';

const initialState = {
        cardData: {},
        topProjectListing: [],
        topProjectListingLoading: false,
        top10Projects: [],
        top10ProjectsLoading: false,
        businessSectorWiseRevenue: [],
        businessSectorWiseRevenueLoading: false,
        revenueGraph: [],
        top10WithinFY: [],
        loading: false,
        priorityUnitsForFy: [],
        capacityProjectForFY: [],
        projectWiseCustomerGroup: [],
        customerGroupList: [],
        projectWiseCustomerGroupTotal: 10,
        projectWiseCustomerGroupLoading: false,
        projectWiseMonthlyRevenue: null,
        projectWiseMonthlyRevenueLoading: false,
};

export const getDashboardCustomCardDetail = createAsyncThunk('dashboard/getDashboardCustomCardDetail', async (params, { rejectWithValue }) => {
        try {
                // Add month range as 4 individual parameters
                const queryParams = {};
                if (params?.monthRange) {
                        const { from_month, from_year, to_month, to_year } = params.monthRange;
                        if (from_month) queryParams.from_month = from_month;
                        if (from_year) queryParams.from_year = from_year;
                        if (to_month) queryParams.to_month = to_month;
                        if (to_year) queryParams.to_year = to_year;
                }

                const response = await axiosInstance.get(ADMIN_DASHBOARD_CUSTOM_CARD, {
                        params: queryParams,
                });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getDashboardBusinessSectorWiseRevenue = createAsyncThunk('dashboard/getDashboardBusinessSectorWiseRevenue', async (params, { rejectWithValue }) => {
        try {
                // Add month range as 4 individual parameters
                const queryParams = {};
                if (params?.monthRangeBusinessSectorWise) {
                        const { from_month, from_year, to_month, to_year } = params.monthRangeBusinessSectorWise;
                        if (from_month) queryParams.from_month = from_month;
                        if (from_year) queryParams.from_year = from_year;
                        if (to_month) queryParams.to_month = to_month;
                        if (to_year) queryParams.to_year = to_year;
                }
                const response = await axiosInstance.get(ADMIN_DASHBOARD_BUSINESS_SECTORWISE_REVENUE, { params: queryParams });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getDashboardTop10BusinessSectorWise = createAsyncThunk('dashboard/getDashboardTopBusinessSectorWise', async (params, { rejectWithValue }) => {
        try {
                // Add month range as 4 individual parameters
                const queryParams = {};
                if (params.BS_UNIT) {
                        queryParams.BS_UNIT = params.BS_UNIT;
                }

                const response = await axiosInstance.get(ADMIN_TOP_10_PROJECT_LISTING, {
                        params: queryParams,
                });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getDashboardTop10Projects = createAsyncThunk('dashboard/getDashboardTop10Projects', async (params, { rejectWithValue }) => {
        try {
                // Add month range as 4 individual parameters
                const queryParams = {};
                if (params.filterParameter) {
                        queryParams.TYPE = params.filterParameter;
                }

                if (params?.monthRange) {
                        const {
                                from_month,
                                from_year,
                                to_month,
                                to_year,
                        } = params.monthRange;

                        if (from_month) queryParams.from_month = from_month;
                        if (from_year) queryParams.from_year = from_year;
                        if (to_month) queryParams.to_month = to_month;
                        if (to_year) queryParams.to_year = to_year;
                }

                const response = await axiosInstance.get(ADMIN_TOP_10_PROJECTS, {
                        params: queryParams,
                });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getPriorityUnitsForFY = createAsyncThunk('dashboard/getPriorityUnitsForFY', async (_, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(ADMIN_PRIORITY_UNITS);

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getCapacityProjectForFY = createAsyncThunk('dashboard/getCapacityProjectForFY', async (_, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(ADMIN_PRIORITY_UNITS, { params: { type: 'capacity' } });

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getDashboardRevenueGraph = createAsyncThunk('dashboard/getDashboardRevenueGraph', async (params, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(ADMIN_REVENUE_GRAPH);

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getTopTenProjectWithinFY = createAsyncThunk('dashboard/getTopTenProjectWithinFY', async (params, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(ADMIN_TOP_10_PROJECT_WITHIN_FY);

                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getProjectWiseCustomerGroupListing = createAsyncThunk('dashboard/getProjectWiseCustomerGroupListing', async (params, { rejectWithValue }) => {
        // const payload = RemoveEmptyKey({
        //         page: params?.page,
        //         limit: params?.limit,
        //         customer_group_code: params.customerGroupCode,
        //         search: params.search,
        // });
        const payload = RemoveEmptyKey({
                page: params?.page,
                limit: params?.limit,
                customer_group_code: params.customerGroupCode,
                search: params.search,

                sort_by: params?.sort_by,
                sort_to: params?.sort_to,

                from_year: params?.from_year,
                from_month: params?.from_month,

                to_year: params?.to_year,
                to_month: params?.to_month,
        });

        try {
                const response = await axiosInstance.get(ADMIN_PROJECT_REPORT_CUSTOMER_GROUP_WISE_LISTING, {
                        params: payload,
                });
                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getCustomerGroupList = createAsyncThunk('dashboard/getCustomerGroupList', async (_, { rejectWithValue }) => {
        try {
                const response = await axiosInstance.get(ADMIN_CUSTOMER_GROUP_LIST);
                return response.data;
        } catch (error) {
                openNotification(error?.response?.data?.message || 'Something went wrong.');
                return rejectWithValue(error?.response?.data?.message || 'Something went wrong.');
        }
});

export const getProjectWiseMonthlyRevenue = createAsyncThunk(
        'dashboard/getProjectWiseMonthlyRevenue',
        async (_, { rejectWithValue }) => {
                try {
                        const { data } = await axiosInstance.get('/project-wise-report', {
                                params: { FILTER_PARAMETER: 'REVENUE' },
                        });
                        return data;
                } catch (err) {
                        return rejectWithValue(err?.response?.data || err.message);
                }
        }
);

const dashboardSlice = createSlice({
        name: 'dashboard',
        initialState,
        reducers: {},
        extraReducers: (builder) => {
                builder.addCase(getDashboardCustomCardDetail.pending, (state) => {
                        state.loading = true;
                        state.error = null;
                })
                        .addCase(getDashboardCustomCardDetail.fulfilled, (state, action) => {
                                state.loading = false;
                                state.cardData = action.payload?.data || []; // Fallback if nested undefined
                        })

                        .addCase(getDashboardCustomCardDetail.rejected, (state, action) => {
                                state.loading = false;
                                state.error = action.payload;
                        })
                        .addCase(getDashboardTop10BusinessSectorWise.pending, (state) => {
                                state.error = null;
                                state.topProjectListingLoading = true;
                        })
                        .addCase(getDashboardTop10BusinessSectorWise.fulfilled, (state, action) => {
                                state.topProjectListing = action.payload?.data || []; // Fallback if nested undefined
                                state.topProjectListingLoading = false;
                        })

                        .addCase(getDashboardTop10BusinessSectorWise.rejected, (state, action) => {
                                state.error = action.payload;
                                state.topProjectListingLoading = false;
                        })
                        .addCase(getDashboardTop10Projects.pending, (state) => {
                                state.error = null;
                                state.top10ProjectsLoading = true;
                        })
                        .addCase(getDashboardTop10Projects.fulfilled, (state, action) => {
                                state.top10Projects = action.payload?.data || []; // Fallback if nested undefined
                                state.top10ProjectsLoading = false;
                        })

                        .addCase(getDashboardTop10Projects.rejected, (state, action) => {
                                state.error = action.payload;
                                state.top10ProjectsLoading = false;
                        })
                        .addCase(getPriorityUnitsForFY.pending, (state) => {
                                state.error = null;
                        })
                        .addCase(getPriorityUnitsForFY.fulfilled, (state, action) => {
                                state.priorityUnitsForFy = action.payload?.data || []; // Fallback if nested undefined
                        })

                        .addCase(getPriorityUnitsForFY.rejected, (state, action) => {
                                state.error = action.payload;
                        })
                        .addCase(getCapacityProjectForFY.pending, (state) => {
                                state.error = null;
                        })
                        .addCase(getCapacityProjectForFY.fulfilled, (state, action) => {
                                state.capacityProjectForFY = action.payload?.data || []; // Fallback if nested undefined
                        })

                        .addCase(getCapacityProjectForFY.rejected, (state, action) => {
                                state.error = action.payload;
                        })
                        .addCase(getDashboardBusinessSectorWiseRevenue.pending, (state) => {
                                state.error = null;
                                state.businessSectorWiseRevenueLoading = true;
                        })
                        .addCase(getDashboardBusinessSectorWiseRevenue.fulfilled, (state, action) => {
                                state.businessSectorWiseRevenue = action.payload?.data || []; // Fallback if nested undefined
                                state.businessSectorWiseRevenueLoading = false;
                        })

                        .addCase(getDashboardBusinessSectorWiseRevenue.rejected, (state, action) => {
                                state.error = action.payload;
                                state.businessSectorWiseRevenueLoading = false;
                        })
                        .addCase(getDashboardRevenueGraph.pending, (state) => {
                                state.error = null;
                        })
                        .addCase(getDashboardRevenueGraph.fulfilled, (state, action) => {
                                state.revenueGraph = action.payload?.data || []; // Fallback if nested undefined
                        })

                        .addCase(getDashboardRevenueGraph.rejected, (state, action) => {
                                state.error = action.payload;
                        })
                        .addCase(getTopTenProjectWithinFY.pending, (state) => {
                                state.error = null;
                        })
                        .addCase(getTopTenProjectWithinFY.fulfilled, (state, action) => {
                                state.top10WithinFY = action?.payload?.data || []; // Fallback if nested undefined
                        })

                        .addCase(getTopTenProjectWithinFY.rejected, (state, action) => {
                                state.error = action.payload;
                        })
                        .addCase(getProjectWiseCustomerGroupListing.pending, (state) => {
                                state.error = null;
                                state.projectWiseCustomerGroupLoading = true;
                        })
                        .addCase(getProjectWiseCustomerGroupListing.fulfilled, (state, action) => {
                                state.projectWiseCustomerGroup = action?.payload?.data?.data || []; // Fallback if nested undefined
                                state.projectWiseCustomerGroupTotal = action?.payload?.data?.total || []; // Fallback if nested undefined
                                state.projectWiseCustomerGroupLoading = false;

                        })

                        .addCase(getProjectWiseCustomerGroupListing.rejected, (state, action) => {
                                state.error = action.payload;
                                state.projectWiseCustomerGroupLoading = false;

                        })
                        .addCase(getCustomerGroupList.pending, (state) => {
                                state.error = null;
                        })
                        .addCase(getCustomerGroupList.fulfilled, (state, action) => {
                                state.customerGroupList = action?.payload?.data || []; // Fallback if nested undefined
                        })

                        .addCase(getCustomerGroupList.rejected, (state, action) => {
                                state.error = action.payload;
                        })
                        .addCase(getProjectWiseMonthlyRevenue.pending, (state) => {
                                state.projectWiseMonthlyRevenueLoading = true;
                        })
                        .addCase(getProjectWiseMonthlyRevenue.fulfilled, (state, action) => {
                                state.projectWiseMonthlyRevenue = action.payload;
                                state.projectWiseMonthlyRevenueLoading = false;
                        })
                        .addCase(getProjectWiseMonthlyRevenue.rejected, (state) => {
                                state.projectWiseMonthlyRevenueLoading = false;
                        });

        },
});

export default dashboardSlice.reducer;
